"use strict";

/* ============================================================
   GLOBAL CONFIG
============================================================ */

const needs_reload = "#cart-needs-reload";

const disableLoading = () => {
  const container =
    document.getElementById("main-cart-items") ||
    document.getElementById("CartDrawer-CartItems");

  container?.classList.remove("cart__items--disabled");
};

const getSectionInnerHTML = (html, selector) => {
  return new DOMParser()
    .parseFromString(html, "text/html")
    .querySelector(selector)?.innerHTML;
};

/* ============================================================
   MAIN CART-ITEMS COMPONENT
============================================================ */

class CartItems extends HTMLElement {
  constructor() {
    super();

    const el = this;
    const resolveItemKey = (element) =>
      element?.dataset.itemId || element?.dataset.index || "";
    const findLineElement = (key) =>
      document.getElementById(`CartItem-${key}`) ||
      document.getElementById(`CartDrawer-Item-${key}`) ||
      el.querySelector(`.cart-item [data-item-id="${key}"]`)
        ?.closest(".cart-item") ||
      el
        .querySelector(`.cart-item [data-index="${key}"]`)
        ?.closest(".cart-item");

    /* Selling plans */
    for (const select of el.querySelectorAll(
      'select[data-smartrr-selling-plans-select]'
    )) {
      select.addEventListener("change", () => {
        const input = select.parentElement?.querySelector("input");
        if (input) input.value = select.value;

        const regex = /^Cart(?:Drawer-)?Item-(\d+)$/;
        const row = select.closest("tr.cart-item");
        const match = (row?.id ?? "").match(regex);

        const lineNumber = match ? match[1] : "";
        update_attrs(lineNumber, "", { selling_plan: select.value });
      });
    }

    /* Important: Shopify dynamic sections for cart */
    const getSectionsToRender = () => [
      {
        id: "main-cart-items",
        section: document.getElementById("main-cart-items")?.dataset.id,
        selector: ".js-contents",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "cart-live-region-text",
        section: "cart-live-region-text",
        selector: ".shopify-section",
      },
    ];

    el.getSectionsToRender = getSectionsToRender;

    const lineItemStatusElement =
      document.getElementById("shopping-cart-line-item-status") ||
      document.getElementById("CartDrawer-LineItemStatus");
    const drawerEl = document.querySelector("cart-drawer");

    const fetchCartAndRender = async (shouldOpenDrawer = false) => {
      try {
        const res = await fetch("/cart.js", {
          method: "GET",
          headers: { Accept: "application/json" },
        });
        if (!res.ok) throw new Error("Cart fetch failed");
        const cart = await res.json();
        el.render(cart);
        const freeShippingHandles = JSON.parse(
          sessionStorage.getItem("free_shipping_product_handles") || "[]"
        );
        const progressEl = document.querySelector(".free-shipping-progress");
        let threshold =
          (cart.items_subtotal_price ?? cart.total_price ?? 0) / 100;
        for (const item of cart.items ?? []) {
          if (freeShippingHandles.includes(item.handle) && progressEl?.max) {
            threshold = parseFloat(progressEl.max);
            break;
          }
        }
        update_shipping(threshold);
        document.dispatchEvent(
          new CustomEvent("cart:refresh:done", { detail: { cart } })
        );
        if (shouldOpenDrawer && drawerEl?.open) {
          drawerEl.open();
        }
      } catch (err) {
        console.error("[cart-items] Failed to refresh cart", err);
      }
    };

    document.addEventListener("cart:refresh", () =>
      fetchCartAndRender(true)
    );
    document
      .querySelector("cart-drawer")
      ?.addEventListener("cart-drawer:open", () =>
        fetchCartAndRender(false)
      );

    const currentItemCount = () => {
      return [...el.querySelectorAll('[name="updates[]"]')].reduce(
        (total, input) => total + parseInt(input.value),
        0
      );
    };

    /* Quantity updates */
    el.addEventListener(
      "change",
      debounce(({ target }) => {
        const updates = {};
        const key = resolveItemKey(target);
        if (!key) return;
        updates[key] = parseInt(target.value);

        update_attrs(
          key,
          document.activeElement?.getAttribute("name") ?? "",
          { updates }
        );
      }, 300)
    );

    /* Loading overlays */
    const enableLoading = (id) => {
      const container =
        document.getElementById("main-cart-items") ||
        document.getElementById("CartDrawer-CartItems");

      container?.classList.add("cart__items--disabled");

      const overlays = [
        ...el.querySelectorAll(`#CartItem-${id} .loading-overlay`),
        ...el.querySelectorAll(`#CartDrawer-Item-${id} .loading-overlay`),
      ];
      if (!overlays.length) {
        const lineEl = findLineElement(id);
        if (lineEl) overlays.push(...lineEl.querySelectorAll(".loading-overlay"));
      }

      overlays.forEach((overlay) => overlay.classList.remove("hidden"));
      document.activeElement?.blur();
      lineItemStatusElement?.setAttribute("aria-hidden", "false");
    };

    /* Live region updates */
    const updateLiveRegions = (id, itemCount, totalCount) => {
      if (totalCount === 0) return;
      if (itemCount === 0) return;

      if (currentItemCount() !== itemCount) {
        const errorSpan =
          document.getElementById(`Line-item-error-${id}`) ||
          document.getElementById(`CartDrawer-LineItemError-${id}`);

        const quantityInput =
          document.querySelector(`Quantity-${id}`) ||
          document.querySelector(`Drawer-quantity-${id}`);

        const errorText = errorSpan?.querySelector(".cart-item__error-text");

        if (errorText && quantityInput) {
          errorText.innerHTML = cartStrings.quantityError.replace(
            "[quantity]",
            quantityInput.value
          );
        }
      }

      lineItemStatusElement?.setAttribute("aria-hidden", "true");

      const cartStatus =
        document.getElementById("cart-live-region-text") ||
        document.getElementById("CartDrawer-LiveRegionText");

      cartStatus?.setAttribute("aria-hidden", "false");
      setTimeout(() => cartStatus?.setAttribute("aria-hidden", "true"), 1000);
    };

    /* Money formatting */
    const formatMoney = (cents) => {
      const amount = cents / 100;

      return amount.toLocaleString(
        `${window.Shopify.locale}-${window.Shopify.country}`,
        {
          style: "currency",
          currency: window.Shopify.currency?.active || "USD",
        }
      );
    };

    const update_shipping = (subtotal) => {
      const progress = document.querySelector(".free-shipping-progress");
      const label = document.querySelector(".free-shipping-label");
      if (!progress || !label) return;

      const max = parseFloat(progress.max) || 0;
      const value = Math.min(Math.max(Number(subtotal) || 0, 0), max);
      progress.value = value;

      const remaining = Math.max(max - value, 0);
      const unlocked = remaining === 0;
      const remainingText =
        typeof cartStrings?.freeShippingRemaining === "string"
          ? cartStrings.freeShippingRemaining.replace(
              "[amount]",
              formatMoney(remaining * 100)
            )
          : `You're ${formatMoney(remaining * 100)} away from free shipping`;

      label.textContent = unlocked
        ? cartStrings?.freeShippingUnlocked || "Free shipping unlocked"
        : remainingText;
    };

    /* ============================================================
       MAIN UPDATE FUNCTION
    ============================================================ */

    const update_attrs = async (itemId, name, attrs) => {
      enableLoading(itemId);

      const body = JSON.stringify({
        ...attrs,
        sections: getSectionsToRender()
          .map((s) => s.section)
          .filter(Boolean),
        sections_url: location.pathname,
      });

      try {
        const config = fetchConfig();
        const response = await fetch(routes.cart_update_url, {
          ...config,
          body,
        });

        const state = await response.json();

        if (state.errors) throw state.errors;

        const isEmpty =
          state.item_count === 0 ||
          (state.item_count === 1 &&
            state.items[0].handle === "carbon-neutral-order");

        /* Update line prices */
        for (const [index, item] of state.items.entries()) {
          const selector = `#CartDrawer-Item-${item.id} .cart-item__price-wrapper span`;
          const display = el.querySelector(selector);
          if (display) display.textContent = formatMoney(item.final_line_price);
        }

        /* Update checkout total */
        const total = document.querySelector("#CartDrawer-Checkout span");
        if (total) {
          total.textContent = formatMoney(
            isEmpty ? 0 : state.items_subtotal_price
          );
        }

        /* Free shipping widget */
        let threshold = state.items_subtotal_price / 100;
        const freeShippingHandles = JSON.parse(
          sessionStorage.getItem("free_shipping_product_handles") || "[]"
        );

        for (const item of state.items) {
          if (freeShippingHandles.includes(item.handle)) {
            threshold = document.querySelector(".free-shipping-progress").max;
          }
        }

        update_shipping(threshold);

        /* Mark empty state */
        el.classList.toggle("is-empty", isEmpty);

        const drawer = document.querySelector("cart-drawer");
        const footer = document.getElementById("main-cart-footer");

        footer?.classList.toggle("is-empty", isEmpty);
        drawer?.classList.toggle("is-empty", isEmpty);
        const emptyContainer = drawer?.querySelector(".drawer__inner-empty");
        if (emptyContainer) emptyContainer.style.display = isEmpty ? "" : "none";

        /* Replace sections with updated HTML */
        for (const section of getSectionsToRender()) {
          const sectionHtml = state.sections[section.section];
          if (!sectionHtml) continue;
          const target =
            document
              .getElementById(section.id)
              ?.querySelector(section.selector) ||
            document.getElementById(section.id);

          if (target) {
            let sectionContent = getSectionInnerHTML(
              sectionHtml,
              section.selector
            );
            if (sectionContent === undefined) sectionContent = sectionHtml;
            target.innerHTML = sectionContent;
          }
        }

        /* Update live region and focus */
        for (const [id, qty] of Object.entries(attrs.updates || {})) {
          if (qty !== undefined) {
            updateLiveRegions(id, qty, state.item_count);
          }

          const line = findLineElement(id);

          if (line && qty === 0) {
            line.remove();
            continue;
          }

          const focusEl = line?.querySelector(`[name="${name}"]`);
          if (focusEl) {
            if (drawer) {
              trapFocus(drawer, focusEl);
            } else {
              focusEl.focus();
            }
          } else if (isEmpty && drawer) {
            trapFocus(
              drawer.querySelector(".drawer__inner-empty"),
              drawer.querySelector("a")
            );
          }
        }

        disableLoading();

        if (globalThis.location.href.endsWith("/cart")) {
          // Reload logic (if needed)
        }
      } catch (error) {
        const errors =
          document.getElementById("cart-errors") ||
          document.getElementById("CartDrawer-CartErrors");
        if (errors) errors.textContent = String(error);
        disableLoading();
      } finally {
        el.querySelectorAll(".loading-overlay").forEach((w) =>
          w.classList.add("hidden")
        );
        }
      };

    /* Make available externally */
    el.updateQuantity = update_attrs;

    /* Render cart contents (used by cart drawer) */
    el.render = (cart) => {
      const container = el.querySelector("#CartDrawer-CartItems");
      if (!container) return;

      const isEmpty = !cart?.items?.length;

      // Toggle empty state on the drawer + cart list
      const drawer = el.closest("cart-drawer");
      el.classList.toggle("is-empty", isEmpty);
      drawer?.classList.toggle("is-empty", isEmpty);

      // Hide empty state container when cart has items
      const emptyContainer = drawer?.querySelector(".drawer__inner-empty");
      if (emptyContainer) emptyContainer.style.display = isEmpty ? "" : "none";

      // Update checkout total
      const checkoutTotal = document.querySelector("#CartDrawer-Checkout span");
      if (checkoutTotal) {
        checkoutTotal.textContent = formatMoney(
          isEmpty ? 0 : cart.items_subtotal_price
        );
      }

      if (isEmpty) {
        container.innerHTML = "";
        return;
      }

      const itemsHtml = cart.items
        .map((item, index) => {
          const linePrice = formatMoney(item.final_line_price);
          const productUrl = item.url ? `${item.url}?variant=${item.id}` : "#";
          const quantityId = `Drawer-quantity-${item.id}`;
          const lineErrorId = `CartDrawer-LineItemError-${item.id}`;

          return `
            <tr id="CartDrawer-Item-${item.id}" class="cart-item" role="row">
              <td class="cart-item__media" role="cell" headers="CartDrawer-ColumnProductImage">
                <a href="${productUrl}" class="cart-item__link" tabindex="-1" aria-hidden="true"></a>
                <div class="cart-item__image-container">
                  <img class="cart-item__image cart-item__drawer-image" src="${item.image}" alt="${item.product_title}" loading="lazy" width="150" height="188">
                </div>
              </td>

              <td class="cart-item__details" role="cell" headers="CartDrawer-ColumnProduct">
                <a href="${productUrl}" class="cart-item__name h3 break">${item.product_title}</a>
                <div class="product-option visually-hidden">
                  ${linePrice}
                </div>
              </td>

              <td class="cart-item__remove" role="cell" headers="CartDrawer-ColumnTotal">
                <cart-remove-button id="CartDrawer-Remove-${index + 1}" data-index="${index + 1}" data-item-id="${item.id}" class="float-right">
                  <button type="button" class="button" aria-label="Remove ${item.product_title}">
                    <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-remove" width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 7V17C6.90524 17 9.09476 17 13 17V7" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                      <path d="M1 3H15" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                      <path d="M7 1H9" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                    </svg>
                  </button>
                </cart-remove-button>
              </td>

              <td class="cart-item__totals right" role="cell" headers="CartDrawer-ColumnTotal">
                <div class="loading-overlay hidden">
                  <div class="loading-overlay__spinner">
                    <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                      <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                    </svg>
                  </div>
                </div>
                <div class="cart-item__price-wrapper"><span class="price price--end">${linePrice}</span></div>
              </td>

              <td class="cart-item__quantity" role="cell" headers="CartDrawer-ColumnQuantity">
                <div class="cart-item__quantity-wrapper float-right">
                  <quantity-input class="quantity">
                    <button class="quantity__button no-js-hidden" name="minus" type="button">
                      <span class="visually-hidden">Decrease quantity for ${item.product_title}</span>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-minus-skinny">
                        <path d="M5 12H19" stroke="black" stroke-width="2" stroke-linecap="round"></path>
                      </svg>
                    </button>
                    <input class="quantity__input" type="number" name="updates[]" value="${item.quantity}" min="0" aria-label="Quantity for ${item.product_title}" id="${quantityId}" data-index="${index + 1}" data-item-id="${item.id}">
                    <button class="quantity__button no-js-hidden" name="plus" type="button">
                      <span class="visually-hidden">Increase quantity for ${item.product_title}</span>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-plus-skinny">
                        <path d="M12 5V19M5 12H19" stroke="black" stroke-width="2" stroke-linecap="round"></path>
                      </svg>
                    </button>
                  </quantity-input>
                </div>
              </td>

              <td class="cart-item__errors !col-start-1 !col-end-5" role="cell">
                <div id="${lineErrorId}" class="cart-item__error" role="alert">
                  <small class="cart-item__error-text"></small>
                  <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-error" viewBox="0 0 13 13">
                    <circle cx="6.5" cy="6.50049" r="5.5" stroke="white" stroke-width="2"></circle>
                    <circle cx="6.5" cy="6.5" r="5.5" fill="#EB001B" stroke="#EB001B" stroke-width="0.7"></circle>
                    <path d="M5.87413 3.52832L5.97439 7.57216H7.02713L7.12739 3.52832H5.87413ZM6.50076 9.66091C6.88091 9.66091 7.18169 9.37267 7.18169 9.00504C7.18169 8.63742 6.88091 8.34917 6.50076 8.34917C6.12061 8.34917 5.81982 8.63742 5.81982 9.00504C5.81982 9.37267 6.12061 9.66091 6.50076 9.66091Z" fill="white"></path>
                    <path d="M5.87413 3.17832H5.51535L5.52424 3.537L5.6245 7.58083L5.63296 7.92216H5.97439H7.02713H7.36856L7.37702 7.58083L7.47728 3.537L7.48617 3.17832H7.12739H5.87413ZM6.50076 10.0109C7.06121 10.0109 7.5317 9.57872 7.5317 9.00504C7.5317 8.43137 7.06121 7.99918 6.50076 7.99918C5.94031 7.99918 5.46982 8.43137 5.46982 9.00504C5.46982 9.57872 5.94031 10.0109 6.50076 10.0109Z" fill="white" stroke="#EB001B" stroke-width="0.7"></path>
                  </svg>
                </div>
              </td>
            </tr>
          `;
        })
        .join("");

      container.innerHTML = `
        <table class="cart-items" role="table">
          <thead class="visually-hidden" role="rowgroup">
            <tr role="row">
              <th id="CartDrawer-ColumnProductImage" role="columnheader">
                <span class="visually-hidden">Product image</span>
              </th>
              <th id="CartDrawer-ColumnProduct" class="caption-with-letter-spacing" scope="col" role="columnheader">
                Product
              </th>
              <th id="CartDrawer-ColumnTotal" class="right caption-with-letter-spacing" scope="col" role="columnheader">
                Total
              </th>
              <th id="CartDrawer-ColumnQuantity" role="columnheader">
                <span class="visually-hidden">Quantity</span>
              </th>
            </tr>
          </thead>
          <tbody role="rowgroup">
            ${itemsHtml}
          </tbody>
        </table>
      `;
    };
  }
}

/* ============================================================
   DEFINE ELEMENTS
============================================================ */

customElements.define("cart-items", CartItems);

/* Cart Note */
customElements.define(
  "cart-note",
  class extends CartItems {
    constructor() {
      super();
      this.addEventListener("change", async ({ currentTarget }) => {
        if (currentTarget instanceof HTMLTextAreaElement) {
          const body = JSON.stringify({ note: currentTarget.value });
          await fetch(routes.cart_update_url, {
            ...fetchConfig(),
            body,
          });
        }
      });
    }
  }
);

/* Remove button */
customElements.define(
  "cart-remove-button",
  class extends HTMLElement {
    constructor() {
      super();

      this.addEventListener("click", (event) => {
        event.preventDefault();

        const itemKey = this.dataset.itemId || this.dataset.index || "";
        const updates = {};
        if (itemKey) updates[itemKey] = 0;

        // Ensure we always find the <cart-items> element
        const parent =
          this.closest("cart-items") ||
          this.closest("cart-drawer-items") ||
          document.querySelector("cart-items");

        if (!parent || !itemKey) {
          console.error("cart-items component not found for removal");
          return;
        }

        parent.updateQuantity(itemKey, "", { updates });
      });
    }
  }
);
